/**
 * ContextMenu — right-click menu wrapper around Radix Context Menu.
 *
 * Mirrors the pattern of `dropdown-menu.tsx` so usage is consistent. Use it
 * to attach a right-click menu to any element via `<ContextMenuTrigger>`.
 *
 * @example
 *   <ContextMenu>
 *     <ContextMenuTrigger asChild>
 *       <div>right-click me</div>
 *     </ContextMenuTrigger>
 *     <ContextMenuContent>
 *       <ContextMenuLabel>Actions</ContextMenuLabel>
 *       <ContextMenuItem onSelect={() => doSomething()}>
 *         Do something
 *       </ContextMenuItem>
 *     </ContextMenuContent>
 *   </ContextMenu>
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import * as ContextMenuPrimitive from '@radix-ui/react-context-menu'
import { Check, ChevronRight, Circle } from 'lucide-react'

import { cn } from '@/lib/utils'

export const ContextMenu = ContextMenuPrimitive.Root
export const ContextMenuTrigger = ContextMenuPrimitive.Trigger
export const ContextMenuGroup = ContextMenuPrimitive.Group
export const ContextMenuPortal = ContextMenuPrimitive.Portal
export const ContextMenuSub = ContextMenuPrimitive.Sub
export const ContextMenuRadioGroup = ContextMenuPrimitive.RadioGroup

export const ContextMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.SubTrigger>
>(({ className, children, style, ...props }, ref) => (
  <ContextMenuPrimitive.SubTrigger
    ref={ref}
    style={{ ...styles.item, ...style }}
    className={cn('dropdown-item', className)}
    {...props}
  >
    {children}
    <ChevronRight style={styles.chevron} />
  </ContextMenuPrimitive.SubTrigger>
))
ContextMenuSubTrigger.displayName = ContextMenuPrimitive.SubTrigger.displayName

export const ContextMenuSubContent = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.SubContent>
>(({ className, style, ...props }, ref) => (
  <ContextMenuPrimitive.SubContent
    ref={ref}
    style={{ ...styles.content, ...style }}
    className={cn('dropdown-content', className)}
    {...props}
  />
))
ContextMenuSubContent.displayName = ContextMenuPrimitive.SubContent.displayName

export const ContextMenuContent = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Content>
>(({ className, style, ...props }, ref) => (
  <ContextMenuPrimitive.Portal>
    <ContextMenuPrimitive.Content
      ref={ref}
      style={{ ...styles.content, ...style }}
      className={cn('dropdown-content', className)}
      {...props}
    />
  </ContextMenuPrimitive.Portal>
))
ContextMenuContent.displayName = ContextMenuPrimitive.Content.displayName

export const ContextMenuItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Item> & { inset?: boolean }
>(({ className, inset, style, ...props }, ref) => (
  <ContextMenuPrimitive.Item
    ref={ref}
    style={{
      ...styles.item,
      paddingLeft: inset ? '32px' : '8px',
      ...style,
    }}
    className={cn('dropdown-item', className)}
    {...props}
  />
))
ContextMenuItem.displayName = ContextMenuPrimitive.Item.displayName

export const ContextMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.CheckboxItem>
>(({ className, children, checked, style, ...props }, ref) => (
  <ContextMenuPrimitive.CheckboxItem
    ref={ref}
    style={{ ...styles.checkboxItem, ...style }}
    className={cn('dropdown-item', className)}
    checked={checked}
    {...props}
  >
    <span style={styles.indicator}>
      <ContextMenuPrimitive.ItemIndicator>
        <Check style={{ width: '16px', height: '16px' }} />
      </ContextMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </ContextMenuPrimitive.CheckboxItem>
))
ContextMenuCheckboxItem.displayName = ContextMenuPrimitive.CheckboxItem.displayName

export const ContextMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.RadioItem>
>(({ className, children, style, ...props }, ref) => (
  <ContextMenuPrimitive.RadioItem
    ref={ref}
    style={{ ...styles.checkboxItem, ...style }}
    className={cn('dropdown-item', className)}
    {...props}
  >
    <span style={styles.indicator}>
      <ContextMenuPrimitive.ItemIndicator>
        <Circle style={{ width: '8px', height: '8px', fill: 'currentColor' }} />
      </ContextMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </ContextMenuPrimitive.RadioItem>
))
ContextMenuRadioItem.displayName = ContextMenuPrimitive.RadioItem.displayName

export const ContextMenuLabel = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Label> & { inset?: boolean }
>(({ className, inset, style, ...props }, ref) => (
  <ContextMenuPrimitive.Label
    ref={ref}
    style={{
      ...styles.label,
      paddingLeft: inset ? '32px' : '8px',
      ...style,
    }}
    className={className}
    {...props}
  />
))
ContextMenuLabel.displayName = ContextMenuPrimitive.Label.displayName

export const ContextMenuSeparator = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Separator>
>(({ className, style, ...props }, ref) => (
  <ContextMenuPrimitive.Separator
    ref={ref}
    style={{ ...styles.separator, ...style }}
    className={className}
    {...props}
  />
))
ContextMenuSeparator.displayName = ContextMenuPrimitive.Separator.displayName

const styles: Record<string, CSSProperties> = {
  content: {
    zIndex: 50,
    minWidth: '200px',
    overflow: 'hidden',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-secondary)',
    padding: '4px',
    color: 'var(--text-primary)',
    boxShadow: 'var(--shadow-md)',
  },
  item: {
    position: 'relative',
    display: 'flex',
    cursor: 'default',
    userSelect: 'none',
    alignItems: 'center',
    gap: '8px',
    borderRadius: 'var(--radius-sm)',
    padding: '6px 8px',
    fontSize: '13px',
    outline: 'none',
    transition: 'background var(--transition-fast)',
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    color: 'inherit',
  },
  checkboxItem: {
    position: 'relative',
    display: 'flex',
    cursor: 'default',
    userSelect: 'none',
    alignItems: 'center',
    borderRadius: 'var(--radius-sm)',
    padding: '6px 8px 6px 32px',
    fontSize: '13px',
    outline: 'none',
    transition: 'background var(--transition-fast)',
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    color: 'inherit',
  },
  indicator: {
    position: 'absolute',
    left: '8px',
    display: 'flex',
    width: '14px',
    height: '14px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chevron: {
    marginLeft: 'auto',
    width: '16px',
    height: '16px',
  },
  label: {
    padding: '6px 8px',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-muted)',
  },
  separator: {
    margin: '4px -4px',
    height: '1px',
    background: 'var(--border-default)',
  },
}
